import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../firebase.init";
import {
  Copy,
  Download,
  FileText,
  Hash,
  Type,
  AlignLeft,
  Tag,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import "../../assets/css/PremiumTools.css";

const ProductDescriptionGenerator = () => {
  // State management
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [productName, setProductName] = useState("");
  const [productDetails, setProductDetails] = useState("");
  const [language, setLanguage] = useState("English");

  // Generated content sections
  const [productDescription, setProductDescription] = useState("");
  const [featureHighlights, setFeatureHighlights] = useState([]);
  const [specificationDetails, setSpecificationDetails] = useState([]);
  const [seoOptimizedContent, setSeoOptimizedContent] = useState({
    titleOptions: [],
    seoBulletPoints: [],
    seoDescription: "",
  });
  const [metaTags, setMetaTags] = useState({
    title: "",
    description: "",
  });
  const [slug, setSlug] = useState("");
  const [productTags, setProductTags] = useState([]);

  // User data
  const [credits, setCredits] = useState(0);

  // API keys state
  const [apiKeys, setApiKeys] = useState([]);
  
  // Language options - Expanded to 120+ languages
  const languageOptions = [
    { value: "English", label: "English" },
    { value: "Spanish", label: "Spanish" },
    { value: "French", label: "French" },
    { value: "German", label: "German" },
    { value: "Chinese", label: "Chinese" },
    { value: "Japanese", label: "Japanese" },
    { value: "Arabic", label: "العربية (Arabic)" },
    { value: "Hindi", label: "हिन्दी (Hindi)" },
    { value: "Portuguese", label: "Português (Portuguese)" },
    { value: "Russian", label: "Русский (Russian)" },
    { value: "Bengali", label: "বাংলা (Bengali)" },
    { value: "Indonesian", label: "Bahasa Indonesia (Indonesian)" },
    { value: "Urdu", label: "اردو (Urdu)" },
    { value: "Italian", label: "Italiano (Italian)" },
    { value: "Turkish", label: "Türkçe (Turkish)" },
    { value: "Korean", label: "한국어 (Korean)" },
    { value: "Vietnamese", label: "Tiếng Việt (Vietnamese)" },
    { value: "Polish", label: "Polski (Polish)" },
    { value: "Dutch", label: "Nederlands (Dutch)" },
    { value: "Thai", label: "ไทย (Thai)" },
    { value: "Persian", label: "فارسی (Persian)" },
    { value: "Romanian", label: "Română (Romanian)" },
    { value: "Greek", label: "Ελληνικά (Greek)" },
    { value: "Czech", label: "Čeština (Czech)" },
    { value: "Swedish", label: "Svenska (Swedish)" },
    { value: "Hungarian", label: "Magyar (Hungarian)" },
    { value: "Danish", label: "Dansk (Danish)" },
    { value: "Finnish", label: "Suomi (Finnish)" },
    { value: "Hebrew", label: "עברית (Hebrew)" },
    { value: "Norwegian", label: "Norsk (Norwegian)" },
    { value: "Ukrainian", label: "Українська (Ukrainian)" },
    { value: "Malay", label: "Bahasa Melayu (Malay)" },
    { value: "Filipino", label: "Filipino" },
    { value: "Swahili", label: "Kiswahili (Swahili)" },
    { value: "Tamil", label: "தமிழ் (Tamil)" },
    { value: "Telugu", label: "తెలుగు (Telugu)" },
    { value: "Marathi", label: "मराठी (Marathi)" },
    { value: "Gujarati", label: "ગુજરાતી (Gujarati)" },
    { value: "Kannada", label: "ಕನ್ನಡ (Kannada)" },
    { value: "Punjabi", label: "ਪੰਜਾਬੀ (Punjabi)" },
    { value: "Burmese", label: "မြန်မာစာ (Burmese)" },
    { value: "Pashto", label: "پښتو (Pashto)" },
    { value: "Amharic", label: "አማርኛ (Amharic)" },
    { value: "Azerbaijani", label: "Azərbaycanca (Azerbaijani)" },
    { value: "Belarusian", label: "Беларуская (Belarusian)" },
    { value: "Bulgarian", label: "Български (Bulgarian)" },
    { value: "Catalan", label: "Català (Catalan)" },
    { value: "Croatian", label: "Hrvatski (Croatian)" },
    { value: "Estonian", label: "Eesti (Estonian)" },
    { value: "Georgian", label: "ქართული (Georgian)" },
    { value: "Icelandic", label: "Íslenska (Icelandic)" },
    { value: "Irish", label: "Gaeilge (Irish)" },
    { value: "Khmer", label: "ភាសាខ្មែរ (Khmer)" },
    { value: "Lao", label: "ພາສາລາວ (Lao)" },
    { value: "Latvian", label: "Latviešu (Latvian)" },
    { value: "Lithuanian", label: "Lietuvių (Lithuanian)" },
    { value: "Macedonian", label: "Македонски (Macedonian)" },
    { value: "Malayalam", label: "മലയാളം (Malayalam)" },
    { value: "Mongolian", label: "Монгол (Mongolian)" },
    { value: "Nepali", label: "नेपाली (Nepali)" },
    { value: "Sinhala", label: "සිංහල (Sinhala)" },
    { value: "Slovak", label: "Slovenčina (Slovak)" },
    { value: "Slovenian", label: "Slovenščina (Slovenian)" },
    { value: "Albanian", label: "Shqip (Albanian)" },
    { value: "Serbian", label: "Српски (Serbian)" },
    { value: "Somali", label: "Soomaali (Somali)" },
    { value: "Sundanese", label: "Basa Sunda (Sundanese)" },
    { value: "Tajik", label: "Тоҷикӣ (Tajik)" },
    { value: "Uzbek", label: "Oʻzbekcha (Uzbek)" },
    { value: "Yoruba", label: "Yorùbá (Yoruba)" },
    { value: "Zulu", label: "isiZulu (Zulu)" },
    { value: "Afrikaans", label: "Afrikaans" },
    { value: "Akan", label: "Akan" },
    { value: "Assamese", label: "অসমীয়া (Assamese)" },
    { value: "Aymara", label: "Aymar aru (Aymara)" },
    { value: "Bambara", label: "Bamanankan (Bambara)" },
    { value: "Bashkir", label: "Башҡортса (Bashkir)" },
    { value: "Bhojpuri", label: "भोजपुरी (Bhojpuri)" },
    { value: "Chichewa", label: "Chicheŵa (Chichewa)" },
    { value: "Chuvash", label: "Чӑвашла (Chuvash)" },
    { value: "Cornish", label: "Kernewek (Cornish)" },
    { value: "Dhivehi", label: "ދިވެހިބަސް (Dhivehi)" },
    { value: "Dogri", label: "डोगरी (Dogri)" },
    { value: "Esperanto", label: "Esperanto" },
    { value: "Fijian", label: "Vosa Vakaviti (Fijian)" },
    { value: "Frisian", label: "Frysk (Frisian)" },
    { value: "Galician", label: "Galego (Galician)" },
    { value: "Hausa", label: "Hausa (Hausa)" },
    { value: "Hawaiian", label: "ʻŌlelo Hawaiʻi (Hawaiian)" },
    { value: "Hmong", label: "Hmoob (Hmong)" },
    { value: "Igbo", label: "Igbo (Igbo)" },
    { value: "Javanese", label: "Basa Jawa (Javanese)" },
    { value: "Kannada", label: "ಕನ್ನಡ (Kannada)" },
    { value: "Kazakh", label: "Қазақ тілі (Kazakh)" },
    { value: "Kurdish", label: "Kurdî (Kurdish)" },
    { value: "Kyrgyz", label: "Кыргызча (Kyrgyz)" },
    { value: "Luxembourgish", label: "Lëtzebuergesch (Luxembourgish)" },
    { value: "Malagasy", label: "Malagasy" },
    { value: "Maltese", label: "Malti (Maltese)" },
    { value: "Maori", label: "Te Reo Māori (Maori)" },
    { value: "Manipuri", label: "মৈতৈলোন্ (Manipuri)" },
    { value: "Mizo", label: "Mizo ṭawng (Mizo)" },
    { value: "Navajo", label: "Diné bizaad (Navajo)" },
    { value: "Odia", label: "ଓଡ଼ିଆ (Odia)" },
    { value: "Papiamento", label: "Papiamento" },
    { value: "Quechua", label: "Runa Simi (Quechua)" },
    { value: "Samoan", label: "Gagana Samoa (Samoan)" },
    { value: "Scots Gaelic", label: "Gàidhlig (Scots Gaelic)" },
    { value: "Sesotho", label: "Sesotho (Sesotho)" },
    { value: "Shona", label: "ChiShona (Shona)" },
    { value: "Sindhi", label: "سنڌي (Sindhi)" },
    { value: "Tahitian", label: "Reo Tahiti (Tahitian)" },
    { value: "Tatar", label: "Татар теле (Tatar)" },
    { value: "Tibetan", label: "བོད་ཡིག (Tibetan)" },
    { value: "Tigrinya", label: "ትግርኛ (Tigrinya)" },
    { value: "Tsonga", label: "Xitsonga (Tsonga)" },
    { value: "Turkmen", label: "Türkmençe (Turkmen)" },
    { value: "Uyghur", label: "ئۇيغۇرچە (Uyghur)" },
    { value: "Welsh", label: "Cymraeg (Welsh)" },
    { value: "Xhosa", label: "isiXhosa (Xhosa)" },
  ];

  // Clean text by removing markdown symbols
  const cleanText = (text) => {
    return text
      .replace(/\*/g, "") // Remove asterisks
      .replace(/#{1,6}\s?/g, "") // Remove markdown headers
      .replace(/`{1,3}/g, "") // Remove code blocks
      .replace(/\[.*?\]\(.*?\)/g, "") // Remove markdown links
      .replace(/!\[.*?\]\(.*?\)/g, "") // Remove markdown images
      .trim();
  };

  // Fetch API keys from endpoint
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinone-clone-7d5d795e98ce.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) {
          setError("No API keys available. Please contact administrator.");
        }
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Get random API key
  const getRandomApiKey = () => {
    if (apiKeys.length === 0) {
      throw new Error("No API keys available");
    }
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // Fetch user data and API keys when user is authenticated
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allinone-clone-7d5d795e98ce.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
          setCredits(userData.currentBalance);
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Generate meta tags as a fallback
  const generateMetaTags = async (
    productName,
    productDescription,
    language
  ) => {
    try {
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;

      const prompt = `Generate SEO-optimized and reader-focused meta tags for the following product:

Product Name: ${productName}
Product Description: ${productDescription}
Language: ${language}

Please provide:
1. Meta Title (60 characters max): Create a compelling title that includes primary keywords and attracts clicks.
2. Meta Description (160 characters max): Write a reader-focused description that highlights key benefits, includes relevant keywords naturally, and encourages clicks. Make it engaging and informative.

Format your response as:
META TITLE: [title]
META DESCRIPTION: [description]`;

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [
                {
                  text: prompt,
                },
              ],
            },
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 256,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        const generatedText = data.candidates[0].content.parts[0].text;

        // Parse the response
        const titleMatch = generatedText.match(/META TITLE:\s*(.+)/);
        const descMatch = generatedText.match(/META DESCRIPTION:\s*(.+)/);

        return {
          title: titleMatch ? cleanText(titleMatch[1]) : "",
          description: descMatch ? cleanText(descMatch[1]) : "",
        };
      } else {
        throw new Error("No content generated");
      }
    } catch (error) {
      console.error("Error generating meta tags:", error);
      return { title: "", description: "" };
    }
  };

  // Construct the AI prompt
  const constructPrompt = () => {
    return `You are an AI-powered SEO expert specializing in e-commerce product descriptions. 
    Your task is to create a complete SEO-optimized product content package based on the provided information.
    
    Product Name: ${productName}
    Product Details: ${productDetails}
    Language: ${language}
    
    Please generate the following sections:
    
    1. Product Descriptions:
       Write a compelling, keyword-rich description of the product. Keep it engaging, benefit-driven, and conversion-focused.
       Format the description in paragraphs. When you have two or more sentences that form a complete thought, start a new paragraph.
       Do not use any markdown symbols like asterisks (*), hashtags (#), or other formatting.
    
    2. Feature Highlights:
       Provide 5-7 bullet points highlighting the product's main features and benefits.
       Do not use asterisks (*) or any other markdown symbols in the bullet points.
    
    3. Specification Details:
       List detailed technical or physical specifications in bullet points or a clean structured format.
       Do not use asterisks (*) or any other markdown symbols in the specifications.
    
    4. SEO-Optimized Content:
       a. Optimized Titles: Suggest 3 optimized titles for the product.
       b. SEO Bullet Points: Provide 5-7 bullet points optimized for search engines.
       c. SEO Description: Write a product description optimized for search engines.
       Do not use asterisks (*) or any other markdown symbols in any of these elements.
    
    5. Meta Tags:
       a. Meta Title: Provide a recommended meta title (60 characters max). Make it compelling and include primary keywords.
       b. Meta Description: Provide a reader-focused and SEO-friendly meta description (160 characters max). 
          - Provide a reader-focused, SEO-friendly meta description (maximum 160 characters).
          - Emphasize **why someone should choose this product** by highlighting its unique benefits, features, or advantages.
          - Naturally include main keywords.
          - Make it engaging, informative, and click-worthy for potential customers.
    
    6. Slug:
       Suggest an SEO-friendly slug for the product URL.
    
    7. Products Tags:
       Provide 5-7 relevant tags for the product, separated by commas.
    
    Format your response with clear section headers as follows:
    
    ===PRODUCT DESCRIPTIONS===
    [Content]
    
    ===FEATURE HIGHLIGHTS===
    - [Bullet point 1]
    - [Bullet point 2]
    ...
    
    ===SPECIFICATION DETAILS===
    - [Specification 1]
    - [Specification 2]
    ...
    
    ===SEO-OPTIMIZED CONTENT===
    ===OPTIMIZED TITLES===
    [Title 1]
    [Title 2]
    [Title 3]
    
    ===SEO BULLET POINTS===
    - [Bullet point 1]
    - [Bullet point 2]
    ...
    
    ===SEO DESCRIPTION===
    [Content]
    
    ===META TAGS===
    ===META TITLE===
    [Content]
    
    ===META DESCRIPTION===
    [Content]
    
    ===SLUG===
    [Content]
    
    ===PRODUCTS TAGS===
    [Tag 1], [Tag 2], [Tag 3], ...`;
  };

  // Parse the AI response
  const parseResponse = (responseText) => {
    const sections = responseText.split("===");
    const result = {};

    for (let i = 1; i < sections.length; i += 2) {
      const sectionName = sections[i] ? sections[i].trim() : "";
      const sectionContent = sections[i + 1] ? sections[i + 1].trim() : "";

      if (sectionName === "PRODUCT DESCRIPTIONS") {
        // Clean the product description and format as paragraphs
        const cleanedContent = cleanText(sectionContent);
        // Split by double newlines to identify paragraphs
        const paragraphs = cleanedContent
          .split(/\n\s*\n/)
          .filter((p) => p.trim() !== "");
        result.productDescription = paragraphs.join("\n\n");
      } else if (sectionName === "FEATURE HIGHLIGHTS") {
        result.featureHighlights = sectionContent
          .split("\n")
          .filter((line) => line.trim() !== "")
          .map((line) => cleanText(line.replace(/^-\s*/, "")));
      } else if (sectionName === "SPECIFICATION DETAILS") {
        result.specificationDetails = sectionContent
          .split("\n")
          .filter((line) => line.trim() !== "")
          .map((line) => cleanText(line.replace(/^-\s*/, "")));
      } else if (sectionName === "SEO-OPTIMIZED CONTENT") {
        // Parse nested sections
        const nestedSections = sectionContent.split("===");
        const seoContent = {
          titleOptions: [],
          seoBulletPoints: [],
          seoDescription: "",
        };

        for (let j = 1; j < nestedSections.length; j += 2) {
          const nestedName = nestedSections[j] ? nestedSections[j].trim() : "";
          const nestedContent = nestedSections[j + 1]
            ? nestedSections[j + 1].trim()
            : "";

          if (nestedName === "OPTIMIZED TITLES") {
            seoContent.titleOptions = nestedContent
              .split("\n")
              .filter((line) => line.trim() !== "")
              .map((line) => cleanText(line));
          } else if (nestedName === "SEO BULLET POINTS") {
            seoContent.seoBulletPoints = nestedContent
              .split("\n")
              .filter((line) => line.trim() !== "")
              .map((line) => cleanText(line.replace(/^-\s*/, "")));
          } else if (nestedName === "SEO DESCRIPTION") {
            seoContent.seoDescription = cleanText(nestedContent);
          }
        }

        result.seoOptimizedContent = seoContent;
      } else if (sectionName === "META TAGS") {
        // Parse nested sections using a more robust approach
        const metaTags = {
          title: "",
          description: "",
        };

        // Split the META TAGS section into nested sections
        const metaSections = sectionContent.split("===");

        // Iterate through nested sections in pairs (header, content)
        for (let j = 1; j < metaSections.length; j += 2) {
          const metaSectionName = metaSections[j] ? metaSections[j].trim() : "";
          const metaSectionContent = metaSections[j + 1]
            ? metaSections[j + 1].trim()
            : "";

          if (metaSectionName === "META TITLE") {
            metaTags.title = cleanText(metaSectionContent);
          } else if (metaSectionName === "META DESCRIPTION") {
            metaTags.description = cleanText(metaSectionContent);
          }
        }

        result.metaTags = metaTags;
      } else if (sectionName === "SLUG") {
        result.slug = cleanText(sectionContent);
      } else if (sectionName === "PRODUCTS TAGS") {
        // Parse comma-separated tags
        const tagsText = cleanText(sectionContent);
        result.productTags = tagsText
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag !== "");
      }
    }

    return result;
  };

  // Generate a product description package
  const generateDescription = async () => {
    if (!productName.trim() || !productDetails.trim()) {
      setError("Product name and details are required");
      return;
    }
    if (apiKeys.length === 0) {
      setError("No API keys available. Please try again later.");
      return;
    }
    if (credits < 1) {
      setError(
        "Insufficient credits. You need at least 1 credit to generate a product description."
      );
      return;
    }
    setGenerating(true);
    setError("");

    // Reset previous results
    setProductDescription("");
    setFeatureHighlights([]);
    setSpecificationDetails([]);
    setSeoOptimizedContent({
      titleOptions: [],
      seoBulletPoints: [],
      seoDescription: "",
    });
    setMetaTags({
      title: "",
      description: "",
    });
    setSlug("");
    setProductTags([]);

    try {
      // Deduct 1 credit
      const deductResponse = await axios.post(
        `https://allinone-clone-7d5d795e98ce.herokuapp.com/api/deduct-credit`,
        {
          userEmail: user.email,
          amount: 1,
        }
      );
      if (!deductResponse.data.success) {
        throw new Error("Failed to deduct credits");
      }
      // Update credits locally
      setCredits((prev) => prev - 1);

      // Generate description
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [
                {
                  text: prompt,
                },
              ],
            },
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 2048,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        const generatedText = data.candidates[0].content.parts[0].text;
        const parsedContent = parseResponse(generatedText);

        // Check if meta tags were generated properly, if not, generate them separately
        if (
          !parsedContent.metaTags ||
          !parsedContent.metaTags.title ||
          !parsedContent.metaTags.description
        ) {
          const fallbackMetaTags = await generateMetaTags(
            productName,
            parsedContent.productDescription || productDetails,
            language
          );

          if (fallbackMetaTags.title || fallbackMetaTags.description) {
            parsedContent.metaTags = {
              title: fallbackMetaTags.title || "",
              description: fallbackMetaTags.description || "",
            };
          }
        }

        // Update state with parsed content
        if (parsedContent.productDescription) {
          setProductDescription(parsedContent.productDescription);
        }

        if (
          parsedContent.featureHighlights &&
          parsedContent.featureHighlights.length > 0
        ) {
          setFeatureHighlights(parsedContent.featureHighlights);
        }

        if (
          parsedContent.specificationDetails &&
          parsedContent.specificationDetails.length > 0
        ) {
          setSpecificationDetails(parsedContent.specificationDetails);
        }

        if (parsedContent.seoOptimizedContent) {
          setSeoOptimizedContent(parsedContent.seoOptimizedContent);
        }

        if (parsedContent.metaTags) {
          setMetaTags(parsedContent.metaTags);
        }

        if (parsedContent.slug) {
          setSlug(parsedContent.slug);
        }

        if (parsedContent.productTags) {
          setProductTags(parsedContent.productTags);
        }

        toast.success("Product content package generated successfully!");
      } else {
        throw new Error("No content was generated from the API response");
      }
    } catch (err) {
      console.error("Error generating description:", err);
      setError(`Error: ${err.message}`);
      toast.error(`Error: ${err.message}`);

      // Refund credits on failure
      try {
        await axios.post(`https://allinone-clone-7d5d795e98ce.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: 1,
        });
        setCredits((prev) => prev + 1);
      } catch (refundErr) {
        console.error("Error refunding credits:", refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  // Copy text to clipboard
  const copyToClipboard = (text, type) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`${type} copied to clipboard!`);
      })
      .catch((err) => {
        toast.error(`Failed to copy ${type.toLowerCase()} to clipboard`);
        console.error("Error copying text: ", err);
      });
  };

  // Download content as a text file
  const downloadContent = (content, filename) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded successfully!`);
  };

  // Download all content as a package
  const downloadAllContent = () => {
    let allContent = `PRODUCT DESCRIPTION PACKAGE\n\n`;
    allContent += `Product Name: ${productName}\n\n`;

    if (productDescription) {
      allContent += `===PRODUCT DESCRIPTION===\n${productDescription}\n\n`;
    }

    if (featureHighlights && featureHighlights.length > 0) {
      allContent += `===FEATURE HIGHLIGHTS===\n`;
      featureHighlights.forEach((highlight) => {
        allContent += `• ${highlight}\n`;
      });
      allContent += "\n";
    }

    if (specificationDetails && specificationDetails.length > 0) {
      allContent += `===SPECIFICATION DETAILS===\n`;
      specificationDetails.forEach((spec) => {
        allContent += `• ${spec}\n`;
      });
      allContent += "\n";
    }

    if (seoOptimizedContent) {
      if (
        seoOptimizedContent.titleOptions &&
        seoOptimizedContent.titleOptions.length > 0
      ) {
        allContent += `===SEO OPTIMIZED TITLES===\n`;
        seoOptimizedContent.titleOptions.forEach((title) => {
          allContent += `${title}\n`;
        });
        allContent += "\n";
      }

      if (
        seoOptimizedContent.seoBulletPoints &&
        seoOptimizedContent.seoBulletPoints.length > 0
      ) {
        allContent += `===SEO BULLET POINTS===\n`;
        seoOptimizedContent.seoBulletPoints.forEach((point) => {
          allContent += `• ${point}\n`;
        });
        allContent += "\n";
      }

      if (seoOptimizedContent.seoDescription) {
        allContent += `===SEO DESCRIPTION===\n${seoOptimizedContent.seoDescription}\n\n`;
      }
    }

    if (metaTags) {
      if (metaTags.title) {
        allContent += `===META TITLE===\n${metaTags.title}\n\n`;
      }

      if (metaTags.description) {
        allContent += `===META DESCRIPTION===\n${metaTags.description}\n\n`;
      }
    }

    if (slug) {
      allContent += `===SLUG===\n${slug}\n\n`;
    }

    if (productTags && productTags.length > 0) {
      allContent += `===PRODUCTS TAGS===\n`;
      productTags.forEach((tag) => {
        allContent += `#${tag}\n`;
      });
      allContent += "\n";
    }

    downloadContent(
      allContent,
      `${productName.replace(/\s+/g, "-")}-product-content.txt`
    );
  };

  // Save the generated content
  const saveContent = () => {
    if (!productDescription && featureHighlights.length === 0) {
      toast.error("No content to save");
      return;
    }
    // Here you would typically save to your database
    toast.success("Content saved successfully!");
    // For demo purposes, we'll just show a success message
  };

  // Reset form
  const resetForm = () => {
    setProductName("");
    setProductDetails("");
    setLanguage("English");
    setProductDescription("");
    setFeatureHighlights([]);
    setSpecificationDetails([]);
    setSeoOptimizedContent({
      titleOptions: [],
      seoBulletPoints: [],
      seoDescription: "",
    });
    setMetaTags({
      title: "",
      description: "",
    });
    setSlug("");
    setProductTags([]);
    setError("");
  };

  // Buy more credits
  const buyMoreCredits = () => {
    toast("Redirecting to payment page...");
    window.location.href = "/user/select-package";
  };

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loader-wrapper">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3 text-muted fw-medium">Loading...</p>
       
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card text-center">
          <div className="mb-4 icon-box mx-auto bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{width: 60, height: 60}}>
            <Type size={30} />
          </div>
          <h2 className="fw-bold text-dark mb-2">Product Description Generator</h2>
          <p className="text-muted mb-4">Login to start generating descriptions.</p>
          <button className="btn btn-primary btn-lg w-100 fw-semibold" onClick={() => (window.location.href = "/login")}>
            Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="premium-app-container">
      {/* Navbar */}
      <nav className="premium-navbar">
        <div className="container-xl d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <button className="btn-glass-icon" onClick={() => window.history.back()}>&larr;</button>
            <span className="premium-brand">
              <div className="brand-glow-icon"><FileText size={18} color="white" /></div>
              Product Content Generator
            </span>
          </div>
          <div className="credit-pill-glass">
            <span className="text-warning">⚡</span>
            <span>{credits} Credits</span>
            {credits < 5 && <button onClick={buyMoreCredits} className="buy-btn-neon">Add</button>}
          </div>
        </div>
      </nav>

      <div className="text-center my-5">
        <h1 className="display-4 fw-bold text-white mb-3">Product Description</h1>
        <p className="lead text-white-50">This is your AI-powered SEO expert, creating captivating and keyword-rich e-commerce product descriptions to boost your online sales.</p>
      </div>

      {error && <div className="container-xl mt-3"><div className="alert alert-danger">{error}</div></div>}

      {/* Main Content */}
      <div className="container-xl py-4 py-lg-5"
      ><div className="row g-4">
        {/* Left Side - Form Area */}
        <div className="col-lg-4"
        ><div className="glass-card h-100 p-4">
          <div className="mb-4 input-group-glass">
            <label className="d-block text-white mb-2 fw-semibold">Step 1 — Name Of The Product</label>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="e.g., iPhone 12, Samsung Galaxy Note 20, etc."
              className="w-100 bg-transparent border-0 text-white"
              style={{ outline: 'none' }}
            />
          </div>

          <div className="mb-4 input-group-glass">
            <label className="d-block text-white mb-2 fw-semibold">Step 2 — Enter A Few Details About The Product</label>
            <textarea
              value={productDetails}
              onChange={(e) => setProductDetails(e.target.value)}
              placeholder="Please describe your product, including its key features, benefits, and any other relevant details."
              rows={5}
              className="w-100 bg-transparent border-0 text-white"
              style={{ outline: 'none', resize: 'none' }}
            />
          </div>

          <div className="mb-4">
            <h3 className="h5 text-white mb-3">
              Step 3 – Advanced Settings (optional)
            </h3>
            <div className="form-group">
              <label htmlFor="language" className="form-label text-white-50">
                Select Language
              </label>
              <div className="select-wrapper">
                <select
                  id="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="form-select input-group-glass"
                >
                  {languageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="select-arrow"></div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-neon-primary w-100"
              onClick={generateDescription}
              disabled={
                generating ||
                !productName.trim() ||
                !productDetails.trim() ||
                credits < 1 ||
                apiKeys.length === 0
              }
            >
              {generating ? (
                <>
                  <span className="btn-spinner"></span>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <span>Create Content</span>
                </>
              )}
            </button>
            <button type="button" className="btn btn-outline-light w-100 mt-2" onClick={resetForm}>
              Reset
            </button>
          </div>
        </div>

        </div>
        {/* Right Side - Result Editor */}
        <div className="col-lg-8"
        ><div className="glass-card h-100 d-flex flex-column">
          <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-secondary">
            <h2 className="h3 text-white mb-0">Your Result</h2>
            <div className="d-flex gap-2">
              <button
                className="btn btn-neon-primary d-flex align-items-center gap-2"
                onClick={downloadAllContent}
                disabled={!productDescription && featureHighlights.length === 0}
              >
                <Download size={16} />
                <span>Download All</span>
              </button>
            </div>
          </div>

          <div className="editor-container">
                        {generating ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-5 my-5">
                 <div className="spinner-border text-primary mb-3" style={{width: '3rem', height: '3rem'}} role="status"></div>
                 <p className="text-white-50">AI is generating your product content package...</p>
              </div>
            ) : (
              <div className="generated-content">
                {/* Product Descriptions Section */}
                {productDescription && (
                  <div className="glass-card p-4 mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom border-secondary">
                      <div className="d-flex align-items-center gap-2">
                        <FileText size={18} className="text-primary" />
                        <h3 className="h4 text-white mb-0">Product Descriptions</h3>
                      </div>
                      <button
                        className="btn btn-outline-secondary btn-sm text-white"
                        onClick={() =>
                          copyToClipboard(
                            productDescription,
                            "Product Descriptions"
                          )
                        }
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                    <div className="section-content product-description-content">
                      {productDescription
                        .split("\n\n")
                        .map((paragraph, index) =>
                          paragraph.trim() !== "" ? (
                            <p key={index}>{paragraph}</p>
                          ) : null
                        )}
                    </div>
                  </div>
                )}

                {/* Feature Highlights Section */}
                {featureHighlights && featureHighlights.length > 0 && (
                  <div className="glass-card p-4 mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom border-secondary">
                      <div className="d-flex align-items-center gap-2">
                        <AlignLeft size={18} className="text-primary" />
                        <h3 className="h4 text-white mb-0">Feature Highlights</h3>
                      </div>
                      <button
                        className="btn btn-outline-secondary btn-sm text-white"
                        onClick={() =>
                          copyToClipboard(
                            featureHighlights
                              .map((item) => `• ${item}`)
                              .join("\n"),
                            "Feature Highlights"
                          )
                        }
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                    <div className="section-content">
                      <ul className="list-unstyled text-white-50">
                        {featureHighlights.map((highlight, index) => (
                          <li key={index}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Specification Details Section */}
                {specificationDetails && specificationDetails.length > 0 && (
                  <div className="glass-card p-4 mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom border-secondary">
                      <div className="d-flex align-items-center gap-2">
                        <FileText size={18} className="text-primary" />
                        <h3 className="h4 text-white mb-0">Specification Details</h3>
                      </div>
                      <button
                        className="btn btn-outline-secondary btn-sm text-white"
                        onClick={() =>
                          copyToClipboard(
                            specificationDetails
                              .map((item) => `• ${item}`)
                              .join("\n"),
                            "Specification Details"
                          )
                        }
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                    <div className="section-content">
                      <ul className="spec-list">
                        {specificationDetails.map((spec, index) => (
                          <li key={index}>{spec}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* SEO-Optimized Content Section */}
                {seoOptimizedContent &&
                  ((seoOptimizedContent.titleOptions &&
                    seoOptimizedContent.titleOptions.length > 0) ||
                    (seoOptimizedContent.seoBulletPoints &&
                      seoOptimizedContent.seoBulletPoints.length > 0) ||
                    seoOptimizedContent.seoDescription) && (
                    <div className="glass-card p-4 mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom border-secondary">
                        <div className="d-flex align-items-center gap-2">
                          <FileText size={18} className="text-primary" />
                          <h3 className="h4 text-white mb-0">
                            SEO-Optimized Content
                          </h3>
                        </div>
                        <button
                          className="btn btn-outline-secondary btn-sm text-white"
                          onClick={() => {
                            let content = "";
                            if (
                              seoOptimizedContent.titleOptions &&
                              seoOptimizedContent.titleOptions.length > 0
                            ) {
                              content +=
                                "Optimized Titles:\n" +
                                seoOptimizedContent.titleOptions.join("\n") +
                                "\n\n";
                            }
                            if (
                              seoOptimizedContent.seoBulletPoints &&
                              seoOptimizedContent.seoBulletPoints.length > 0
                            ) {
                              content +=
                                "SEO Bullet Points:\n" +
                                seoOptimizedContent.seoBulletPoints
                                  .map((item) => `• ${item}`)
                                  .join("\n") +
                                "\n\n";
                            }
                            if (seoOptimizedContent.seoDescription) {
                              content +=
                                "SEO Description:\n" +
                                seoOptimizedContent.seoDescription;
                            }
                            copyToClipboard(content, "SEO-Optimized Content");
                          }}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                      <div className="section-content">
                        {/* Optimized Titles */}
                        {seoOptimizedContent.titleOptions &&
                          seoOptimizedContent.titleOptions.length > 0 && (
                            <div className="mb-4">
                              <h4 className="h5 text-info mb-3">
                                Optimized Titles
                              </h4>
                              <ul className="list-unstyled text-white-50">
                                {seoOptimizedContent.titleOptions.map(
                                  (title, index) => (
                                    <li key={index}>{title}</li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}

                        {/* SEO Bullet Points */}
                        {seoOptimizedContent.seoBulletPoints &&
                          seoOptimizedContent.seoBulletPoints.length > 0 && (
                            <div className="mb-4">
                              <h4 className="h5 text-info mb-3">
                                SEO Bullet Points
                              </h4>
                              <ul className="list-unstyled text-white-50">
                                {seoOptimizedContent.seoBulletPoints.map(
                                  (point, index) => (
                                    <li key={index}>{point}</li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}

                        {/* SEO Description */}
                        {seoOptimizedContent.seoDescription && (
                          <div className="mb-4">
                            <h4 className="h5 text-info mb-3">
                              SEO Description
                            </h4>
                            <p>{seoOptimizedContent.seoDescription}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Meta Tags Section - Always show after generation */}
                {!generating &&
                  (productDescription ||
                    featureHighlights.length > 0 ||
                    specificationDetails.length > 0 ||
                    (seoOptimizedContent.titleOptions &&
                      seoOptimizedContent.titleOptions.length > 0) ||
                    (seoOptimizedContent.seoBulletPoints &&
                      seoOptimizedContent.seoBulletPoints.length > 0) ||
                    seoOptimizedContent.seoDescription ||
                    slug ||
                    (productTags && productTags.length > 0)) && (
                    <div className="content-section meta-tags-section">
                      <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom border-secondary">
                        <div className="d-flex align-items-center gap-2">
                          <Type size={18} className="text-primary" />
                          <h3 className="h4 text-white mb-0">Meta Tags</h3>
                        </div>
                        <div className="meta-actions"></div>
                      </div>
                      <div className="section-content">
                        {/* Meta Title */}
                        <div className="mb-4">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h4 className="h6 text-white mb-0">Meta Title</h4>
                            <div className="d-flex align-items-center gap-3">
                              <span
                                className={`char-count ${
                                  metaTags.title && metaTags.title.length > 60
                                    ? "warning"
                                    : ""
                                }`}
                              >
                                {metaTags.title ? metaTags.title.length : 0}/60
                                characters
                              </span>
                              <button
                                className="btn btn-link text-white-50 p-0"
                                onClick={() =>
                                  copyToClipboard(
                                    metaTags.title || "",
                                    "Meta Title"
                                  )
                                }
                                title="Copy Meta Title"
                              >
                                <Copy size={14} />
                              </button>
                            </div>
                          </div>
                          <div className="meta-content">
                            <div className="bg-dark bg-opacity-50 p-3 rounded border border-secondary">
                              <p className="meta-title">{metaTags.title}</p>
                              <div className="meta-preview">
                                <div className="browser-tab">
                                  <div className="browser-tab-favicon"></div>
                                  <div className="browser-tab-title">
                                    {metaTags.title
                                      ? metaTags.title.length > 60
                                        ? metaTags.title.substring(0, 60) +
                                          "..."
                                        : metaTags.title
                                      : "Product Title"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Meta Description */}
                        <div className="mb-4">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h4 className="h6 text-white mb-0">Meta Description</h4>
                            <div className="d-flex align-items-center gap-3">
                              <span
                                className={`char-count ${
                                  metaTags.description &&
                                  metaTags.description.length > 160
                                    ? "warning"
                                    : ""
                                }`}
                              >
                                {metaTags.description
                                  ? metaTags.description.length
                                  : 0}
                                /160 characters
                              </span>
                              <button
                                className="btn btn-link text-white-50 p-0"
                                onClick={() =>
                                  copyToClipboard(
                                    metaTags.description || "",
                                    "Meta Description"
                                  )
                                }
                                title="Copy Meta Description"
                              >
                                <Copy size={14} />
                              </button>
                            </div>
                          </div>
                          <div className="meta-content">
                            <div className="bg-dark bg-opacity-50 p-3 rounded border border-secondary">
                              <p className="text-white mb-3">
                                {metaTags.description}
                              </p>
                              <div className="meta-preview">
                                <div className="bg-black bg-opacity-50 p-3 rounded mt-2">
                                  <div className="text-primary fs-5 mb-1">
                                    {metaTags.title || "Product Title"}
                                  </div>
                                  <div className="text-success small mb-1">
                                    example.com/products/
                                    {slug || "product-slug"}
                                  </div>
                                  <div className="text-white-50 small">
                                    {metaTags.description
                                      ? metaTags.description.length > 160
                                        ? metaTags.description.substring(
                                            0,
                                            160
                                          ) + "..."
                                        : metaTags.description
                                      : "Product description will appear here..."}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Slug Section */}
                {slug && (
                  <div className="glass-card p-4 mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom border-secondary">
                      <div className="d-flex align-items-center gap-2">
                        <Hash size={18} className="text-primary" />
                        <h3 className="h4 text-white mb-0">Slug</h3>
                      </div>
                      <button
                        className="btn btn-outline-secondary btn-sm text-white"
                        onClick={() => copyToClipboard(slug, "Slug")}
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                    <div className="section-content">
                      <div className="bg-dark bg-opacity-50 p-3 rounded border border-secondary">
                        <p className="text-white mb-3">{slug}</p>
                        <div className="bg-black bg-opacity-50 p-3 rounded">
                          <span className="text-success small">
                            example.com/products/
                          </span>
                          <span className="text-white small">{slug}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Products Tags Section */}
                {productTags && productTags.length > 0 && (
                  <div className="glass-card p-4 mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom border-secondary">
                      <div className="d-flex align-items-center gap-2">
                        <Tag size={18} className="text-primary" />
                        <h3 className="h4 text-white mb-0">Products Tags</h3>
                      </div>
                      <div className="meta-actions">
                        <button
                          className="btn btn-outline-secondary btn-sm text-white"
                          onClick={() => {
                            const tagsText = productTags
                              .map((tag) => `#${tag}`)
                              .join(", ");
                            copyToClipboard(tagsText, "Products Tags");
                          }}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="section-content">
                      <div className="bg-dark bg-opacity-50 p-3 rounded border border-secondary">
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {productTags.map((tag, index) => (
                            <span key={index} className="badge bg-primary bg-opacity-25 text-primary border border-primary">
                              #{tag}
                            </span>
                          ))}
                        </div>
                        <div className="bg-black bg-opacity-50 p-3 rounded">
                          <p className="text-white-50 small mb-0">
                            {productTags.map((tag) => `#${tag}`).join(", ")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom CSS */}
      
      </div>
      </div>
    </div>
  );
};

export default ProductDescriptionGenerator;
